import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getActiveSessions } from '@adornis/baseql/server/analytics/activeUsers.js';
import { watchEntireCollection } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { watchQueueStatus } from '@adornis/pub-sub/tasks/server/watchQueueStatus.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { BehaviorSubject, map, switchMap } from 'rxjs';
import { checkPermissionMiddleware } from '../../permissions/middlewares/checkPermissionMiddleware.js';
import { Permission } from '../../permissions/roles.js';
import { sepaExportLock } from '../../sepa/server/export/resolveExportSepa.js';
import { JobQueueStatus } from '../entities/JobQueueStatus.js';
import { personImportLock } from '../../users/import/api/PersonImportAPI.js';

/**
 * How many tasks of a queue wait for a worker and how many it is running. Generic on purpose: the dashboard asks for
 * each of its queues by name, a new queue needs no new operation.
 */
const subscribeQueueStatus = registerSubscription({
  operationName: 'subscribeQueueStatus',
  type: () => JobQueueStatus,
  params: [{ name: 'queue', type: () => String }],
  middlewares: [checkPermissionMiddleware(Permission.READ_PLATFORM_USAGE, GLOBAL_CONTEXT)],
  resolve: (queue: string) => (_gqlFields: BaseQLSelectionSet<JobQueueStatus>) =>
    watchQueueStatus(queue).pipe(map(status => new JobQueueStatus(status))),
});

const subscribeServerTime = registerSubscription({
  operationName: 'subscribeServerTime',
  type: () => String,
  middlewares: [checkPermissionMiddleware(Permission.READ_PLATFORM_USAGE, GLOBAL_CONTEXT)],
  resolve: function () {
    return () => {
      const serverTime = new BehaviorSubject<string>(
        new Date().toLocaleTimeString('de-DE', {
          weekday: 'long',
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }),
      );
      setInterval(() => {
        serverTime.next(
          new Date().toLocaleTimeString('de-DE', {
            weekday: 'long',
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }),
        );
      }, 1000);
      return serverTime.asObservable();
    };
  },
});

const subscribeActiveSessions = registerSubscription({
  operationName: 'subscribeActiveSessions',
  type: () => Int,
  middlewares: [checkPermissionMiddleware(Permission.READ_PLATFORM_USAGE, GLOBAL_CONTEXT)],
  resolve: () => () => watchEntireCollection('sessions').pipe(switchMap(() => getActiveSessions())),
});

const isPersonImportLocked = registerSubscription({
  operationName: 'isPersonImportLocked',
  type: () => Boolean,
  middlewares: [checkPermissionMiddleware(Permission.READ_PLATFORM_USAGE, GLOBAL_CONTEXT)],
  resolve: () => () => personImportLock.asObservable(),
});

const isSepaExportLocked = registerSubscription({
  operationName: 'isSepaExportLocked',
  type: () => Boolean,
  middlewares: [checkPermissionMiddleware(Permission.READ_PLATFORM_USAGE, GLOBAL_CONTEXT)],
  resolve: () => () => sepaExportLock.asObservable(),
});

const unlockPersonImport = registerMutation({
  operationName: 'unlockPersonImport',
  type: () => Boolean,
  middlewares: [checkPermissionMiddleware(Permission.READ_PLATFORM_USAGE, GLOBAL_CONTEXT)],
  resolve: () => () => {
    personImportLock.next(false);
    return true;
  },
});

const unlockSepaExport = registerMutation({
  operationName: 'unlockSepaExport',
  type: () => Boolean,
  middlewares: [checkPermissionMiddleware(Permission.READ_PLATFORM_USAGE, GLOBAL_CONTEXT)],
  resolve: () => () => {
    sepaExportLock.next(false);
    return true;
  },
});

export const UsageAPI = {
  queueStatus: subscribeQueueStatus,
  serverTime: subscribeServerTime,
  activeSessions: subscribeActiveSessions,
  isPersonImportLocked,
  isSepaExportLocked,
  unlockPersonImport,
  unlockSepaExport,
};
